import { useState, useMemo, useEffect } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import ActionButton from '../action-button';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { LibraryAddOutlined } from '@mui/icons-material';
import { useEventSessionsQuery } from '@/state/queries/sessions/use-event-sessions';
import { MAX_SESSION_DURATION_MINUTES } from '@/constants';
import { dateToMinutesFromMidnight } from '@/helpers/date.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import DuplicateSessionForm from './duplicate-session.form';
import { useFlowMode } from '../../../use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { getObjectId } from '@/helpers/object.id.helper';

export default function AddFromLibraryModal({
  offset: initialOffset,
  duration: durationInput,
  setSelectedSlot,
  isUntimed = false,
  insertBeforeSlotId,
}: {
  offset: number;
  duration?: number;
  setSelectedSlot: (slot?: FlowSlotDto) => void;
  isUntimed?: boolean;
  insertBeforeSlotId?: string;
}) {
  const t = useTranslations();
  const flow = useFlowStore((s) => s.flow);
  const isMultiFlowEvent = useEventStore((store) => store.event?.multiFlow);
  const eventSessionsPlan = useEventStore((store) => store.eventSessionsPlan);
  const setEventSessions = useEventStore((store) => store.setEventSessions);
  const mode = useFlowMode();
  const eventIdForLibrary =
    mode === 'PLAN'
      ? flow?.parent && 'origin' in flow.parent
        ? getObjectId(flow.parent.origin)
        : flow?.parent?.id
      : undefined;
  const [modalOpen, setModalOpen] = useState(false);
  const librarySessionsFromStore = useMemo(
    () => eventSessionsPlan?.filter((session) => session.isInLibrary) ?? [],
    [eventSessionsPlan],
  );
  // Fetch library sessions only when the modal opens; until then use event store from the main sessions list.
  const { data: sessions, normalized: normalizedSessions } = useEventSessionsQuery({
    eventId: eventIdForLibrary,
    isInLibrary: true,
    loadAllModes: false,
    enabled: modalOpen && !!eventIdForLibrary,
  });
  const [duration, setDuration] = useState<string>(
    minuteOffsetToTime(Math.min(durationInput ?? 0, MAX_SESSION_DURATION_MINUTES)),
  );

  const librarySessions =
    modalOpen && normalizedSessions.preferredSessions.length > 0
      ? normalizedSessions.preferredSessions
      : librarySessionsFromStore;

  const sessionIdsNotUsedYet = useMemo(() => {
    const alreadyUsedIds = new Set(
      flow?.slots
        .filter((slot) => slot.contentType === 'Session' && slot.content?.id !== undefined)
        .map((e) => e.content?.id) ?? [],
    );
    return librarySessions.filter((e) => !alreadyUsedIds.has(e.id));
  }, [flow?.slots, librarySessions]);

  useEffect(() => {
    if (!sessions) return;
    if (normalizedSessions.preferredSessions.length > 0) {
      setEventSessions(normalizedSessions.preferredSessions);
    }
  }, [sessions, normalizedSessions, setEventSessions]);

  const offset = useMemo(
    () => minuteOffsetToTime((initialOffset ?? 0) + (flow?.slot?.startOffsetMinutes ?? 0)),
    [initialOffset, flow?.slot?.startOffsetMinutes],
  );

  const { durationMinutes, offsetMinutes } = useMemo(() => {
    return {
      durationMinutes: dateToMinutesFromMidnight(duration),
      offsetMinutes: dateToMinutesFromMidnight(offset) - (flow?.slot?.startOffsetMinutes ?? 0),
    };
  }, [duration, offset, flow?.slot?.startOffsetMinutes]);

  if (isMultiFlowEvent === false) {
    return null;
  }

  return (
    <>
      <ActionButton
        disabled={
          isOrganizeMode(mode) ||
          (librarySessions.length > 0 && sessionIdsNotUsedYet.length === 0) ||
          (modalOpen && sessionIdsNotUsedYet.length === 0)
        }
        icon={<LibraryAddOutlined fontSize="small" />}
        onClick={() => setModalOpen(true)}
        label={t('flows.detail.slotList.actions.add')}
        dataTestId="add-session-button"
      />
      <Modal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={t('flows.detail.slotList.actions.addFromLibrary.modalTitle').toUpperCase()}
        description={t('flows.detail.slotList.actions.addFromLibrary.modalDescription')}
      >
        <DuplicateSessionForm
          offset={offsetMinutes}
          durationMinutes={durationMinutes}
          onClose={() => setModalOpen(false)}
          onSuccess={() => setModalOpen(false)}
          sessionsOverride={sessionIdsNotUsedYet}
          setDuration={setDuration}
          setSelectedSlot={setSelectedSlot}
          isUntimed={isUntimed}
          insertBeforeSlotId={insertBeforeSlotId}
        />
      </Modal>
    </>
  );
}
